import React, { useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { Card, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { object, ref, string } from 'yup';

import { ResetForm, ServerError } from '@components/Helper';
import { FormGroupControl } from '@components/Base';
import { selectCurrentUser } from '@components/Auth';
import { UserOtpCodeFormGroupControl } from '@components/Feature/User';

import { selectPasswordPage } from './selector';
import { passwordUpdate, UpdatePasswordPayload } from './action';
import { yupOtpCode } from '@utils/yup';

type FormValues = UpdatePasswordPayload;

const initialValues: FormValues = {
  password: '',
  passwordConfirmation: '',
  otpCode: '',
};

const groupProps = { className: 'mb-3' };

const PasswordPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const { serverErrors, isSuccess } = useSelector(selectPasswordPage);
  const [shouldRenderAlert, setShouldRenderAlert] = useState(isSuccess);

  const validationSchema = object({
    password: string().required(t('validation:required')).min(6, t('validation:min')),
    passwordConfirmation: string()
      .required(t('validation:required'))
      .oneOf([ref('password')], 'passwordConfirmation not match'),
    otpCode: yupOtpCode(),
  });

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    dispatch(passwordUpdate(values));
    setSubmitting(false);
  };

  useEffect(() => {
    setShouldRenderAlert(isSuccess);
  }, [isSuccess]);

  return (
    <section>
      <h5 className="mb-4">{t('common:password')}</h5>
      {shouldRenderAlert && (
        <Alert variant="success" onClose={() => setShouldRenderAlert(false)} dismissible>
          Your password has been updated.
        </Alert>
      )}
      <Card>
        <Card.Body>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting }) => (
              <Form>
                <FormGroupControl
                  type="password"
                  name="password"
                  label={t('common:password')}
                  groupProps={groupProps}
                />
                <FormGroupControl
                  type="password"
                  name="passwordConfirmation"
                  label="Password confirmation"
                  groupProps={groupProps}
                />
                {currentUser && <UserOtpCodeFormGroupControl mobileNumber={currentUser.mobileNumber} />}
                <Button type="submit" disabled={isSubmitting}>
                  {t('common:update')}
                </Button>
                <ServerError errors={serverErrors} />
                <ResetForm reset={isSuccess} />
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </section>
  );
};

export { PasswordPage };
